import { FC, useCallback } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";
import __ from "src/utils/Translations";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import {
  BillingCycleConfigurationType,
  IAbsenceTypeForm,
  SubmitLimitationType,
  SubmitLimitationPeriodType,
  SubmitLimitation,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";

interface SubmitLimitationOption {
  id: string;
  label: string;
  value: SubmitLimitation;
}

const createSubmitLimitationsOptions = (
  direction: "prior" | "advance"
): Record<string, SubmitLimitationOption[]> => {
  const prefix = direction === "prior" ? "Poprzedni" : "Następny";
  const prefixPlural = direction === "prior" ? "Poprzednie" : "Następne";

  const baseOptions: SubmitLimitationOption[] = [
    {
      id: SubmitLimitationPeriodType.WEEK,
      label: `${prefix} tydzień`,
      value: {
        type: SubmitLimitationType.RELATIVE,
        count: 1,
        periodType: SubmitLimitationPeriodType.WEEK,
      },
    },
    {
      id: "current-month",
      label: "Bieżący miesiąc",
      value: {
        type: SubmitLimitationType.RELATIVE,
        count: 0,
        periodType: SubmitLimitationPeriodType.MONTH,
      },
    },
    {
      id: SubmitLimitationPeriodType.MONTH,
      label: `${prefix} miesiąc`,
      value: {
        type: SubmitLimitationType.RELATIVE,
        count: 1,
        periodType: SubmitLimitationPeriodType.MONTH,
      },
    },
  ];

  const extendedOptions: SubmitLimitationOption[] = [
    {
      id: SubmitLimitationPeriodType.WEEK,
      label: `${prefix} tydzień`,
      value: {
        type: SubmitLimitationType.RELATIVE,
        count: 1,
        periodType: SubmitLimitationPeriodType.WEEK,
      },
    },
    {
      id: "current-month",
      label: "Bieżący miesiąc",
      value: {
        type: SubmitLimitationType.RELATIVE,
        count: 0,
        periodType: SubmitLimitationPeriodType.MONTH,
      },
    },
    {
      id: SubmitLimitationPeriodType.MONTH,
      label: `${prefix} miesiąc`,
      value: {
        type: SubmitLimitationType.RELATIVE,
        count: 1,
        periodType: SubmitLimitationPeriodType.MONTH,
      },
    },
    {
      id: SubmitLimitationPeriodType.QUARTER,
      label: `${prefix} kwartał`,
      value: {
        type: SubmitLimitationType.RELATIVE,
        count: 1,
        periodType: SubmitLimitationPeriodType.QUARTER,
      },
    },
    {
      id: SubmitLimitationPeriodType.HALF_YEAR,
      label: `${prefixPlural} półrocze`,
      value: {
        type: SubmitLimitationType.RELATIVE,
        count: 1,
        periodType: SubmitLimitationPeriodType.HALF_YEAR,
      },
    },
    {
      id: "current-year",
      label: "Bieżący rok",
      value: {
        type: SubmitLimitationType.RELATIVE,
        count: 0,
        periodType: SubmitLimitationPeriodType.YEAR,
      },
    },
    {
      id: SubmitLimitationPeriodType.YEAR,
      label: `${prefix} rok kalendarzowy`,
      value: {
        type: SubmitLimitationType.RELATIVE,
        count: 1,
        periodType: SubmitLimitationPeriodType.YEAR,
      },
    },
  ];

  const advanceOnlyOptions: SubmitLimitationOption[] =
    direction === "advance"
      ? [
          {
            id: "4-years",
            label: "Następne 4 lata",
            value: {
              type: SubmitLimitationType.RELATIVE,
              count: 4,
              periodType: SubmitLimitationPeriodType.YEAR,
            },
          },
        ]
      : [];

  return {
    [BillingCycleConfigurationType.Monthly]: baseOptions,
    [BillingCycleConfigurationType.Yearly]: extendedOptions,
    default: [...extendedOptions, ...advanceOnlyOptions],
  };
};

const submitLimitationsPriorOptions = createSubmitLimitationsOptions("prior");
const submitLimitationsAdvanceOptions =
  createSubmitLimitationsOptions("advance");

const SubmitLimitations: FC = () => {
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );
  const { control } = useFormContext<IAbsenceTypeForm>();

  const billingCycleConfigurationType = useWatch({
    name: "billingCycleConfiguration.type",
  });
  const chosenParentAbsenceType = useWatch({
    name: "billingCycleConfiguration.parentAbsenceTypeId",
  });

  const findOptionByValue = useCallback(
    (
      options: SubmitLimitationOption[],
      currentValue: SubmitLimitation | undefined
    ): SubmitLimitationOption | undefined =>
      options.find(
        (option) =>
          option.value.type === currentValue?.type &&
          option.value.count === currentValue?.count &&
          option.value.periodType === currentValue?.periodType
      ),
    []
  );

  const handleSelectChange = useCallback(
    (
      selectedId: string,
      options: SubmitLimitationOption[],
      onChange: (value: SubmitLimitation) => void
    ) => {
      const selectedOption = options.find((option) => option.id === selectedId);

      if (selectedOption) {
        onChange(selectedOption.value);
      }
    },
    []
  );

  return (
    <Fieldset
      legend={__("Dostępny termin składania wniosku")}
      description={__(
        "Wybierz, z jakim opóźnieniem i wyprzedzeniem pracownik będzie mógł złożyć wniosek."
      )}
      helpText={__(
        "Zmiana ograniczenia spowoduje automatyczne anulowanie wniosków wykraczających poza nowy okres."
      )}
    >
      <FormControl fullWidth margin="normal">
        <Controller
          name="configurationOptions.submitLimitations.prior"
          control={control}
          render={({ field, fieldState: { error, invalid } }) => {
            const currentOptions =
              submitLimitationsPriorOptions[
                billingCycleConfigurationType ?? "default"
              ];

            return (
              <>
                <InputLabel id="prior-select-label" required error={invalid}>
                  {__("Liczba dni wstecz")}
                </InputLabel>
                <Select
                  {...field}
                  labelId="prior-select-label"
                  input={<OutlinedInput label={__("Liczba dni wstecz")} />}
                  error={!!error}
                  disabled={
                    !canWriteAbsencesConfiguration || !!chosenParentAbsenceType
                  }
                  value={
                    findOptionByValue(currentOptions, field.value)?.id || ""
                  }
                  onChange={(e) => {
                    handleSelectChange(
                      e.target.value as string,
                      currentOptions,
                      field.onChange
                    );
                  }}
                  data-testid="absence-type-form-field__submit-limitations-prior"
                >
                  {currentOptions.map(({ id, label }) => (
                    <MenuItem key={id} value={id}>
                      {__(label)}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error={invalid}>
                  {error?.message}
                </FormHelperText>
              </>
            );
          }}
        />
      </FormControl>
      <FormControl fullWidth margin="normal">
        <Controller
          name="configurationOptions.submitLimitations.advance"
          control={control}
          render={({ field, fieldState: { error, invalid } }) => {
            const currentOptions =
              submitLimitationsAdvanceOptions[
                billingCycleConfigurationType ?? "default"
              ];

            return (
              <>
                <InputLabel id="advance-select-label" required error={invalid}>
                  {__("Liczba dni do przodu")}
                </InputLabel>
                <Select
                  {...field}
                  labelId="advance-select-label"
                  input={<OutlinedInput label={__("Liczba dni do przodu")} />}
                  error={!!error}
                  disabled={
                    !canWriteAbsencesConfiguration || !!chosenParentAbsenceType
                  }
                  value={
                    findOptionByValue(currentOptions, field.value)?.id || ""
                  }
                  onChange={(e) => {
                    handleSelectChange(
                      e.target.value as string,
                      currentOptions,
                      field.onChange
                    );
                  }}
                  data-testid="absence-type-form-field__submit-limitations-advance"
                >
                  {currentOptions.map(({ id, label }) => (
                    <MenuItem key={id} value={id}>
                      {__(label)}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error={invalid}>
                  {error?.message}
                </FormHelperText>
              </>
            );
          }}
        />
      </FormControl>
    </Fieldset>
  );
};

export default SubmitLimitations;
